// SnackbarContext.tsx
import { Alert, Snackbar } from "@mui/material";
import React, { createContext, ReactNode, useContext, useState } from "react";

type SnackbarType = "success" | "error" | "info";

type SnackbarContextType = {
  showSnackbar: (message: string, type: SnackbarType) => void;
  closeSnackbar: () => void;
  snackbar: {
    isOpen: boolean;
    message: string;
    type: SnackbarType | null;
  };
};

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

type SnackbarProviderProps = {
  children: ReactNode;
};

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({
  children,
}) => {
  const [snackbar, setSnackbar] = useState({
    isOpen: false,
    message: "",
    type: null as SnackbarType | null,
  });

  const showSnackbar = (message: string, type: SnackbarType) => {
    setSnackbar({
      isOpen: true,
      message,
      type,
    });
  };

  const closeSnackbar = () => {
    setSnackbar({
      isOpen: false,
      message: "",
      type: null,
    });
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar, closeSnackbar, snackbar }}>
      {children}
      <Snackbar
        open={snackbar.isOpen}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.type || "info"} onClose={closeSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};
